import { equals, pathOr } from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useMapZoom } from './useMapZoom';
import { useMaxPinsPerCluster } from './useMaxPinsPerCluster';
import { getFeatureWithSelectedState, getSelectedFeatureSignature } from './usePinsDataSync.utils';
import { IPinsLayer } from './usePinsLayer';
import { setDominantSubdomain } from '../../../../common/actions/results';
import { setSelectedFeature } from '../../../../common/actions/selectFeature/selectFeature';
import { selectFreckles } from '../../../../common/selectors/freckles/selectFreckles';
import { selectResultsDominantSubdomain } from '../../../../common/selectors/results';
import { IFrecklesFeaturesMap } from '../../../../common/types/freckles';
import { IFeaturesMap, IRBushItem, TFeature } from '../../../../common/types/map';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import {
  expandClusterPinFeature,
  geoJsonFeatureToRBushItem,
  geoJsonFrecklesFeatureToRBushItem,
} from '../../../../common/utils/features';
import { mode } from '../../../../common/utils/mode';
import { getFrecklesPointsTilesToUpdate, getTilesToUpdate } from '../../../../common/utils/tiles';
import { selectActiveFeature, selectMapBounds } from '../../../selectors';
import { selectFeaturesForPrecision } from '../../../selectors/features/selectFeaturesForPrecision';
import { TMapBounds } from '../../../types/mapBounds';

interface IUsePinsDataSyncParams extends IPinsLayer {
  map: YMaps.Map;
  isDetailsVisible: boolean;
}

// Чуть больше троттла синка mapBounds (300мс в useBoundsSync): cleanup эффекта
// переносит ребилд, пока серия зумов (колесо/тач) продолжает менять стор,
// поэтому пересборка выполняется один раз после окончания серии и вне анимации
const PINS_REBUILD_DEBOUNCE_MS = 350;

export function usePinsDataSync({
  map,
  rbush,
  rbushFreckles,
  pointsLayer,
  frecklesPointsLayer,
  hotspotsLayer,
  balloonManager,
  isDetailsVisible,
}: IUsePinsDataSyncParams): void {
  const dispatch = useDispatch<TThunkDispatch>();
  const { bounds, zoom, precision } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const mapZoom = useMapZoom(map);
  const maxPinsPerCluster = useMaxPinsPerCluster();
  const selectedFeature = useSelector<IApplicationState, TFeature | null>(selectActiveFeature);
  const subdomain = useSelector<IApplicationState, string>(selectResultsDominantSubdomain);

  const features = useSelector<IApplicationState, IFeaturesMap>(selectFeaturesForPrecision);
  const isFeaturesBucketReady = useSelector<IApplicationState, boolean>(state =>
    Boolean(state.map.features[state.map.mapBounds.precision]),
  );
  const frecklesFeatures: IFrecklesFeaturesMap = useSelector(selectFreckles);

  const subdomainFeatures = useSelector<IApplicationState, IFeaturesMap>(state =>
    pathOr<IFeaturesMap>({}, [precision, 'features'], state.map.features),
  );

  const propertiesFeatures = useSelector<IApplicationState, IFeaturesMap>(state =>
    state.map.features[state.map.mapBounds.precision] ? state.map.features[state.map.mapBounds.precision].features : {},
  );
  const prevFeaturesRef = React.useRef<IFeaturesMap>(propertiesFeatures);
  const rebuildSignatureRef = React.useRef('');
  const lastFeaturesRef = React.useRef<IFeaturesMap>(features);

  React.useEffect(() => {
    if (!bounds) {
      return;
    }

    const featuresInBbox = rbush.search({
      minX: bounds[0][0],
      minY: bounds[0][1],
      maxX: bounds[1][0],
      maxY: bounds[1][1],
    });

    const nextSubdomain = mode(featuresInBbox.map(f => f.feature.properties.subdomain));
    if (!nextSubdomain || nextSubdomain === subdomain) {
      return;
    }

    dispatch(setDominantSubdomain(nextSubdomain));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds, subdomainFeatures]);

  React.useEffect(() => {
    const prevFeatures = prevFeaturesRef.current;
    const updatedFeatures = Object.values(propertiesFeatures).filter(({ id, properties }) => {
      const prevFeature = prevFeatures[id];
      if (!prevFeature) {
        return false;
      }

      if (!equals(properties, prevFeature.properties)) {
        return true;
      }

      return false;
    });

    // в сторе у фич координаты центра кластера, в rbush — раскрытые координаты пинов,
    // поэтому поиск только по id (объектов ≤ ~28, линейный обход дешёвый)
    const rbushItemsAll = rbush.all();
    const rbushItemsToUpdate: IRBushItem[] = [];
    for (const feature of updatedFeatures) {
      const featureId = feature.id as string;
      const featureInRbush = rbushItemsAll.find(({ feature: { id } }) => id === featureId);
      if (featureInRbush) {
        featureInRbush.feature.properties = feature.properties;
        rbushItemsToUpdate.push(featureInRbush);

        // балун-пин перекрашиваем точечно и синхронно (как hover), не дожидаясь дебаунс-пересборки;
        // getById отсекает фичи без цены и ещё не добавленные в менеджер после смены зума
        if (balloonManager.objectManager.objects.getById<TFeature>(featureId)) {
          balloonManager.updateBalloonProperties(featureId, feature.properties, maxPinsPerCluster);
        }
      }
    }

    if (rbushItemsToUpdate.length > 0) {
      const tilesToUpdate = getTilesToUpdate({
        rbushItems: rbushItemsToUpdate,
        bounds,
        zoom,
        includeShadow: true,
      });
      pointsLayer.layer.update(tilesToUpdate);
    }

    prevFeaturesRef.current = propertiesFeatures;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertiesFeatures]);

  React.useEffect(() => {
    // Пересборка отложена и схлопывается (cleanup отменяет невыполненную):
    // removeAll/add в ObjectManager во время зум-анимации карты теряются рендером ymaps,
    // а смена зума и приезд свежих фич происходят почти одновременно — дебаунс
    // выполняет одну пересборку после завершения анимации вместо двух во время неё.
    const timeoutId = window.setTimeout(() => {
      // При смене зума precision меняется раньше, чем приезжает ответ бека — бакета фич
      // ещё нет в сторе. Чтобы слой пинов не исчезал на время полёта запроса, раскрываем
      // последний известный набор кластеров: их центры корректны на любом зуме.
      // Если бакет есть, но пуст — это честно пустой ответ, пины снимаются.
      const isTransitionalEmptyBucket = !isFeaturesBucketReady && Object.keys(features).length === 0;
      const sourceFeatures = isTransitionalEmptyBucket ? lastFeaturesRef.current : features;

      if (Object.keys(sourceFeatures).length > 0) {
        lastFeaturesRef.current = sourceFeatures;
      }

      // На один зум-жест эффект срабатывает дважды (смена зума, затем приезд фич из ответа) —
      // если зум, лимит и видимые данные пинов не изменились, пересборка не нужна:
      // сигнатура покрывает всё, что влияет на координаты и вёрстку балуна
      const selectedFeatureSignature = getSelectedFeatureSignature(selectedFeature);
      const signature = `${mapZoom}|${maxPinsPerCluster}|${selectedFeatureSignature}|${Object.values(sourceFeatures)
        .map(feature => {
          const offer = feature.properties.offers[0];

          return `${feature.id}:${offer?.cianId}:${offer?.averagePricePerDay}:${offer?.isViewed}:${offer?.isFavorite}`;
        })
        .join(';')}`;

      if (signature === rebuildSignatureRef.current) {
        return;
      }
      rebuildSignatureRef.current = signature;

      // полная пересборка балунов (removeAll + add) и на зум-тик — осознанный размен:
      // объектов во вьюпорте ≤ ~28, точечный сдвиг геометрий не оправдан
      const expandedFeatures = Object.values(sourceFeatures)
        .map(feature => getFeatureWithSelectedState(feature, selectedFeature))
        .map(feature => expandClusterPinFeature(feature, mapZoom, maxPinsPerCluster))
        .filter((feature): feature is TFeature => feature !== null);
      const nextSelectedFeature = selectedFeature
        ? (expandedFeatures.find(feature => feature.properties.isActive) ?? null)
        : null;
      const rbushItems = expandedFeatures.map(feature => geoJsonFeatureToRBushItem(feature));

      rbush.clear();

      if (rbushItems.length === 0) {
        // updateBalloons по пустому rbush снимает все балуны с карты
        balloonManager.updateBalloons(maxPinsPerCluster);
        pointsLayer.layer.update();

        return;
      }

      rbush.load(rbushItems);

      hotspotsLayer.layer.update();
      pointsLayer.layer.update();
      balloonManager.updateBalloons(maxPinsPerCluster);

      if (nextSelectedFeature) {
        dispatch(setSelectedFeature({ ...nextSelectedFeature, precision }));
      }
    }, PINS_REBUILD_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, isFeaturesBucketReady, mapZoom, maxPinsPerCluster, selectedFeature]);

  React.useEffect(() => {
    rbushFreckles.clear();

    const rbushFrecklesItems = Object.values(frecklesFeatures).map(geoJsonFrecklesFeatureToRBushItem);

    if (rbushFrecklesItems.length === 0 || zoom >= 13) {
      frecklesPointsLayer.layer.update();

      return;
    }

    rbushFreckles.load(rbushFrecklesItems);

    /*
      bounds может быть undefined в момент bboxToMapBounds, когда MapBoundsUpdater ещё не
      задиспатчил актуальные bounds. Проверка защищает от краша.
    */
    if (bounds) {
      const tilesToUpdate = getFrecklesPointsTilesToUpdate({
        rbushItems: rbushFrecklesItems,
        bounds,
        zoom,
      });

      if (tilesToUpdate.length > 0) {
        frecklesPointsLayer.layer.update(tilesToUpdate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frecklesFeatures, zoom, bounds]);
}
